import React, { FC, useMemo } from 'react';

import { useGetVault } from './hooks/useGetVault';
import { useRefreshVaults } from './hooks/useRefreshVaults';
import { useHandleSupplyClick } from './hooks/useHandleSupplyClick';
import { useSupplyProgress } from './hooks/useSupplyProgress';

import { formatSmart, formatWithMaxPrecision } from '@/shared/utils/helpers';
import { Vault } from '@/shared/api/vault';
import {
    AssetWithIcon,
    Button,
    ButtonVariant,
    Progress,
    Skeleton,
    TooltipWrapperDisabled,
} from '@/shared/ui';
import { useOpenConnectWalletModal } from '@/shared/hooks/useOpenConnectWalletModal';

type AssetsToLendRowsProps = {
    allItems: Vault[];
    itemIndex: number;
};

export const VaultDepositItemRow: FC<AssetsToLendRowsProps> = ({ itemIndex, allItems }) => {
    const {
        address,
        title,
        leverage,
        netApy,
        totalDeposited,
        totalDepositThreshold,
        noWalletInfo,
        curator,
        poolUtilization,
        suspended,
        errorContract,
    } = useGetVault(allItems, itemIndex);

    const formattedApyRewards = Number(netApy || '0').toFixed(2);
    const currentApy = Number(formattedApyRewards);

    const refreshVaults = useRefreshVaults();

    const handleSupplyClick = useHandleSupplyClick(
        leverage,
        address,
        currentApy,
        totalDeposited!,
        totalDepositThreshold!,
        poolUtilization!,
        refreshVaults,
    );

    const supplyProgress = useSupplyProgress(totalDeposited!, totalDepositThreshold!);

    const totalSupply = useMemo(() => {
        if (!totalDepositThreshold) {
            return <Skeleton />;
        }

        return formatWithMaxPrecision(totalDepositThreshold as string, leverage.decimals);
    }, [totalDepositThreshold, leverage?.decimals]);

    const handleConnectWalletClick = useOpenConnectWalletModal();

    const supplyBtn = (
        <Button
            variant={ButtonVariant.Primary}
            onClick={handleSupplyClick}
            className='w-24 border-none'
            disabled={errorContract || !leverage || suspended}
        >
            Supply
        </Button>
    );

    return (
        <>
            <tr className='h-[72px] border-t border-gray-300 bg-white hover:bg-gray-100 [&_td]:font-semibold'>
                <td className='pl-6'>{title}</td>
                <td>
                    {leverage?.symbol ? (
                        <AssetWithIcon
                            assetName={leverage?.symbol}
                            className='[&_img]:h-[24px] [&_img]:w-[24px] [&_div]:text-[14px]'
                        />
                    ) : (
                        <Skeleton />
                    )}
                </td>
                <td>
                    <div className='mt-2 mr-8'>
                        {!noWalletInfo ? (
                            <>
                                <div>
                                    {typeof totalSupply === 'string' ||
                                    typeof totalSupply === 'number'
                                        ? formatSmart(totalSupply)
                                        : totalSupply}{' '}
                                    {`${leverage.symbol}`}
                                </div>
                                <Progress percent={supplyProgress} />
                            </>
                        ) : (
                            <span className='flex'>
                                <Skeleton className='mr-4' />
                                {leverage?.symbol || ''}
                            </span>
                        )}
                    </div>
                </td>
                <td>
                    <div>{Number.isNaN(currentApy) ? 'N/A' : `${currentApy} %`}</div>
                </td>
                <td>
                    <div>{curator}</div>
                </td>
                <td>
                    <div className='flex justify-end gap-5 mr-6'>
                        {noWalletInfo ? (
                            <Button
                                variant={ButtonVariant.Primary}
                                onClick={handleConnectWalletClick}
                                name='connect-wallet'
                                className='flex justify-center items-center w-[152px] text-md h-[44px]'
                            >
                                Connect Wallet
                            </Button>
                        ) : errorContract ? (
                            <TooltipWrapperDisabled
                                text='Vault Contract is not available'
                                component={supplyBtn}
                                sideOffset={10}
                                className='w-[100%]'
                            />
                        ) : (
                            supplyBtn
                        )}
                    </div>
                </td>
            </tr>
        </>
    );
};
